import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EventSummary } from '@common/schemas/entities/summary.schema';

const HOVER_DELAY_MS = 3500;
type CardState = 'collapsed' | 'hover-expanded' | 'pinned';

interface TimelineEventCardProps {
  event: EventSummary;
  entityNameMap: Record<string, string>;
  index: number;
  side: 'left' | 'right';
  onDelete: (id: string) => void;
  onHeightChange?: (id: string, height: number) => void;
  dragHandleProps?: Record<string, any>;
}

function EntityBadges({ event, entityNameMap, limit }: {
  event: EventSummary;
  entityNameMap: Record<string, string>;
  limit?: number;
}) {
  const allIds = useMemo(() => [
    ...(event.characterIds ?? []),
    ...(event.organizationIds ?? []),
    ...(event.locationIds ?? []),
  ], [event]);
  const shown = limit != null ? allIds.slice(0, limit) : allIds;
  const extra = limit != null ? allIds.length - shown.length : 0;
  if (allIds.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
      {shown.map(id => (
        <Chip key={id} label={entityNameMap[id] ?? id} size="small" variant="outlined"
          sx={{ fontSize: '0.68rem', height: 20, borderRadius: 1 }} />
      ))}
      {extra > 0 && <Chip label={`+${extra}`} size="small" sx={{ fontSize: '0.68rem', height: 20, borderRadius: 1 }} />}
    </Box>
  );
}

export function TimelineEventCard({ event, entityNameMap, index, side, onDelete, onHeightChange, dragHandleProps }: TimelineEventCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cardState, setCardState] = useState<CardState>('collapsed');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const isExpanded = cardState !== 'collapsed';

  useEffect(() => {
    if (cardState !== 'pinned') return;
    const handler = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) setCardState('collapsed');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [cardState]);

  useEffect(() => {
    if (!cardRef.current || !onHeightChange) return;
    const observer = new ResizeObserver((entries) => {
      onHeightChange(event.id, entries[0].contentRect.height);
    });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [event.id, onHeightChange]);

  const handleMouseEnter = useCallback(() => {
    if (cardState === 'pinned') return;
    hoverTimer.current = setTimeout(() => setCardState('hover-expanded'), HOVER_DELAY_MS);
  }, [cardState]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (cardState === 'hover-expanded') setCardState('collapsed');
  }, [cardState]);

  const handleClick = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (cardState === 'pinned') navigate({ to: `/events/view/${event.id}` });
    else setCardState('pinned');
  }, [cardState, event.id, navigate]);

  const connector = side === 'left'
    ? { right: -21, width: 20, height: '2px', top: 26 }
    : { left: -21, width: 20, height: '2px', top: 26 };

  return (
    <Box>
      <Box ref={cardRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}
        sx={{
          position: 'relative',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: isExpanded ? 'primary.main' : 'divider',
          borderRadius: 2.5,
          cursor: 'pointer',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isExpanded ? '0 8px 32px rgba(0,0,0,0.4)' : 1,
          '&:hover': { borderColor: 'primary.light', boxShadow: 4, transform: 'translateY(-2px)' },
          // Connector line to spine
          '&::after': {
            content: '""',
            position: 'absolute',
            bgcolor: isExpanded ? 'primary.main' : 'divider',
            ...connector,
          },
        }}
      >
        {/* Header row */}
        <Box sx={{ p: '14px 16px', display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box {...(dragHandleProps || {})} onClick={e => e.stopPropagation()} sx={{
            cursor: 'grab', color: 'text.disabled', mt: 0.5, flexShrink: 0,
            '&:hover': { color: 'primary.main' }, '&:active': { cursor: 'grabbing' },
          }}>
            <DragIndicatorIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{
                fontSize: '1rem',
                color: isExpanded ? 'primary.main' : 'text.primary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {event.name}
              </Typography>
              {/* Edit + Delete — together at top right in expanded mode */}
              <Collapse in={isExpanded} orientation="horizontal" unmountOnExit>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <IconButton size="small" onClick={e => { e.stopPropagation(); navigate({ to: `/events/edit/${event.id}` }); }}
                    sx={{ bgcolor: 'action.hover', borderRadius: 1.5, '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' } }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={e => { e.stopPropagation(); setDeleteOpen(true); }}
                    sx={{ bgcolor: 'action.hover', borderRadius: 1.5, '&:hover': { bgcolor: 'error.main', color: 'error.contrastText' } }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Collapse>
            </Box>

            {/* THE ONE LABEL: 2 lines max when collapsed, full text when expanded */}
            {event.shortDescription && (
              <Typography variant="body2" color="text.secondary" sx={{
                display: '-webkit-box',
                WebkitLineClamp: isExpanded ? 'unset' : 2,
                WebkitBoxOrient: 'vertical',
                overflow: isExpanded ? 'visible' : 'hidden',
                lineHeight: 1.6,
                fontSize: '0.85rem',
                mt: 0.75,
                transition: 'all 0.3s ease',
              }}>
                {event.shortDescription}
              </Typography>
            )}

            <EntityBadges event={event} entityNameMap={entityNameMap} limit={isExpanded ? undefined : 6} />
          </Box>
        </Box>
      </Box>


      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} onClick={e => e.stopPropagation()}>
        <DialogTitle>{t('entity_form.events.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('entity_form.events.delete_desc')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={() => { setDeleteOpen(false); onDelete(event.id); }}>
            {t('entity_form.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order label */}
      <Box sx={{ display: 'none' }}>{index + 1}</Box>
    </Box>
  );
}
